import React, { memo, useEffect, useState } from 'react';
import {
  Pagination,
  PaginationCompact,
  PaginationDisplayedItemCount,
  Dropdown,
  MenuItem,
  useBreakpoint,
} from '@one-thd/sui-atomic-components';

export interface PaginationBarProps {
  totalItems: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Page-size choices offered by the "N / Page" dropdown. Provide together with `onPageSizeChange` to show it. */
  pageSizeOptions?: number[];
  /** Called with the newly selected page size; omit together with `pageSizeOptions` to hide the dropdown. */
  onPageSizeChange?: (pageSize: number) => void;
}

const PaginationBarComponent: React.FC<PaginationBarProps> = memo(
  ({ totalItems, page, pageSize, onPageChange, pageSizeOptions, onPageSizeChange }: PaginationBarProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pagination-bar.ready');
    }, []);

    // SUI's Dropdown renders a Menu that calls @floating-ui/react-dom's useFloating()
    // unconditionally, even while closed — under SSR that throws (React error #321: invalid hook
    // call) and takes down the page. Deferring the dropdown's mount until after client-side
    // render keeps that hook out of SSR entirely.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);

    const { lesser: isCompact } = useBreakpoint('md');
    const count = Math.max(1, Math.ceil(totalItems / pageSize));
    const showPageSizeOptions = Boolean(pageSizeOptions?.length && onPageSizeChange);
    const handlePageChange = (_event: React.ChangeEvent<HTMLDivElement>, nextPage: number) => onPageChange(nextPage);
    const handlePageSizeChange = (_event: React.ChangeEvent<HTMLDivElement>, nextPageSize: number) =>
      onPageSizeChange?.(nextPageSize);

    return (
      <div className="sui-flex sui-w-full sui-flex-wrap sui-items-center sui-justify-between sui-gap-3">
        <div className="sui-flex sui-items-center sui-gap-3">
          {showPageSizeOptions && hasMounted && (
            <div className="sui-w-40 sui-shrink-0">
              <Dropdown value={pageSize} onChange={handlePageSizeChange}>
                {(pageSizeOptions as number[]).map((size) => (
                  <MenuItem key={size} value={size}>{`${size} / Page`}</MenuItem>
                ))}
              </Dropdown>
            </div>
          )}
          <div className="sui-whitespace-nowrap">
            <PaginationDisplayedItemCount items={totalItems} itemsPerPage={pageSize} page={page} disableGrow />
          </div>
        </div>
        {isCompact ? (
          <PaginationCompact count={count} page={page} onChange={handlePageChange} />
        ) : (
          <Pagination count={count} page={page} onChange={handlePageChange} />
        )}
      </div>
    );
  }
);

PaginationBarComponent.displayName = 'PaginationBar';

const PaginationBar = PaginationBarComponent;

export { PaginationBar };
